import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** ProjectSwitcher — sidebar list of projects with create/rename/delete/activate. */
export default function ProjectSwitcher({ activeId, onSwitch }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/ph360/projects");
      setProjects(data.projects || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const createNew = async (e) => {
    e?.preventDefault();
    const title = (newTitle || "Untitled Project").trim() || "Untitled Project";
    try {
      const { data } = await api.post("/ph360/projects", { title });
      toast.success("Project created");
      setNewTitle(""); setCreating(false);
      onSwitch(data.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const activate = async (id) => {
    if (id === activeId) return;
    try {
      const { data } = await api.post(`/ph360/projects/${id}/activate`);
      toast.success("Project switched");
      onSwitch(data.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const rename = async (id, currentTitle) => {
    const title = window.prompt("Rename project", currentTitle || "");
    if (!title) return;
    try {
      await api.patch(`/ph360/projects/${id}`, { title: title.trim() });
      await load();
      if (id === activeId) onSwitch(id); // trigger reload of active project
      toast.success("Renamed");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id, title) => {
    if (!window.confirm(`Delete project "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/ph360/projects/${id}`);
      toast.success("Project deleted");
      // If deleted active, backend already switched to another
      await load();
      const nextActive = (projects.filter(p => p.id !== id)[0] || {}).id;
      if (nextActive) onSwitch(nextActive);
      else onSwitch(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="ef360-project-switcher" data-testid="project-switcher">
      <div className="ef360-project-switcher-title">Projects</div>
      {loading ? <div className="ef360-project-empty">Loading…</div> : projects.length === 0 ? (
        <div className="ef360-project-empty">No projects yet.</div>
      ) : (
        <ul className="ef360-project-list">
          {projects.map(p => (
            <li key={p.id} className={`ef360-project-item ${p.is_active ? "active" : ""}`} data-testid={`project-item-${p.id}`}>
              <button
                className="ef360-project-item-title"
                onClick={()=>activate(p.id)}
                title={p.title}
                data-testid={`project-activate-${p.id}`}
              >
                {p.is_active ? "● " : ""}{p.title}
              </button>
              <div className="ef360-project-item-meta">{p.chapters} ch · by {p.author || "—"}</div>
              <div className="ef360-project-item-actions">
                <button onClick={()=>rename(p.id, p.title)} data-testid={`project-rename-${p.id}`}>Rename</button>
                <button onClick={()=>remove(p.id, p.title)} data-testid={`project-delete-${p.id}`}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {creating ? (
        <form onSubmit={createNew} className="ef360-project-new">
          <input
            type="text"
            placeholder="New project title"
            value={newTitle}
            onChange={(e)=>setNewTitle(e.target.value)}
            autoFocus
            data-testid="project-new-title"
          />
          <div style={{display:"flex", gap:4, marginTop:4}}>
            <button type="submit" className="button button-primary" data-testid="project-new-save">Create</button>
            <button type="button" className="button" onClick={()=>{setCreating(false); setNewTitle("");}}>Cancel</button>
          </div>
        </form>
      ) : (
        <button className="button button-primary ef360-project-new-btn" onClick={()=>setCreating(true)} data-testid="project-new">
          + New project
        </button>
      )}
    </div>
  );
}
